import React from 'react';
import { Link } from 'react-router-dom';
import { applyTypography } from '@repo/common';

const sections = [
  {
    chapter: 'Chapter 1 - The basics',
    heading: '1. What this agreement covers',
    body: [
      <>
        These Terms of Service govern the conditions and procedures for using
        the Koready service (the website at{' '}
        <a
          href='http://www.ko-ready.com'
          target='_blank'
          rel='noopener noreferrer'
          className='underline'
        >
          www.ko-ready.com
        </a>{' '}
        and all related services, hereinafter &quot;the Service&quot;) operated
        by Koready (hereinafter &quot;the Company&quot;), and set out the
        rights, obligations, and responsibilities of the Company and users,
        together with any other matters required by applicable law.
      </>,
    ],
  },
  {
    heading: '2. Terms we use in this agreement',
    body: [
      `The following terms are used in these Terms of Service:`,
      `· "Service" means the Korean language learning, Korean culture experiences, career development, Korea settlement support, and all related online and offline services provided by the Company for global users.`,
      `· "User" means any member or non-member who agrees to these Terms and uses the Service.`,
      `· "Member" means a person who provides personal information to the Company, completes membership registration, and uses the Service.`,
      `· "Content" means offline workshops, online live sessions, video courses, downloadable materials, view-only materials, bundles, and other paid or free products provided by the Company.`,
      `· "Digital Content" means video courses, downloadable materials, view-only materials, and similar items provided through information and communications networks, as defined under the Content Industry Promotion Act and the Act on Consumer Protection in Electronic Commerce.`,
      `· "Divisible Digital Content" means Digital Content composed of multiple lectures or units, the provision of which can be divided into parts.`,
      `· "Bundle" means a product consisting of two or more items of Content sold together.`,
    ],
  },
  {
    heading: '3. How we update this agreement',
    body: [
      `① These Terms take effect from the moment a user agrees to them and begins using the Service.`,
      ``,
      `② The Company may amend these Terms within the limits permitted by applicable law, including the Act on the Regulation of Terms and Conditions, the Act on Consumer Protection in Electronic Commerce, the Act on Promotion of Information and Communications Network Utilization and Information Protection, and the Personal Information Protection Act.`,
      ``,
      `③ When the Company amends these Terms, it will notify users of the reason and content of the amendment — through service notices, email, or site announcements — no later than 30 days before the effective date if the changes are unfavorable to users, or no later than 7 days before the effective date in all other cases.`,
      ``,
      `④ If a user continues to use the Service after the amended Terms take effect, the user is deemed to have agreed to the amended Terms. Users who do not agree may terminate their service agreement.`,
    ],
  },
  {
    heading: '4. How to interpret this agreement',
    body: [
      `① Matters not specified in these Terms are governed by applicable law and established commercial practice, including the Act on the Regulation of Terms and Conditions, the Act on Consumer Protection in Electronic Commerce, the Content Industry Promotion Act, and the Personal Information Protection Act.`,
      ``,
      `② The Company may provide these Terms in languages other than Korean, including English. In the event of any conflict between language versions, the Korean version shall prevail, unless the mandatory consumer protection laws of the user's country of habitual residence require otherwise.`,
    ],
  },
  {
    chapter: 'Chapter 2 - Your account and agreement',
    heading: '5. How the service agreement is formed',
    body: [
      `① The service agreement is formed when a user agrees to these Terms and completes membership registration, or when the user begins using the Service.`,
      ``,
      `② The Company may decline an application to use the Service, or terminate the service agreement after the fact, in any of the following circumstances:`,
      `· The registration information contains false statements, omissions, or errors.`,
      `· The user has previously had access restricted due to a violation of these Terms or unauthorized use.`,
      `· The user does not otherwise meet the requirements for registration established by the Company.`,
    ],
  },
  {
    heading: '6. Who can use the Service',
    body: [
      `① The Service is available to users aged 18 and over.`,
      ``,
      `② The Company restricts registration by persons under the age of 18 through date-of-birth verification at the time of registration, and implements technical measures for this purpose.`,
      ``,
      `③ Pursuant to Article 22-2 of the Personal Information Protection Act, the Company does not, as a general rule, collect personal information from children under the age of 14. If the Company becomes aware that a child under 14 has registered, it will promptly destroy the relevant information.`,
    ],
  },
  {
    heading: '7. Managing your account',
    body: [
      `① Members must enter accurate information and keep it up to date.`,
      ``,
      `② Members must manage their accounts and passwords securely and may not transfer, lend, or share them with any other person.`,
      ``,
      `③ If a member becomes aware that their account has been used without authorization, the member must notify the Company immediately at contact@ko-ready.com. The Company may temporarily restrict access to an account in order to protect the user or other users.`,
    ],
  },
  {
    chapter: 'Chapter 3 - Using the Service',
    heading: '8. What the Service includes',
    body: [
      `The services provided by the Company are as follows. Details regarding dates, access periods, prices, refund conditions, and other particulars for each product are posted on the relevant product page.`,
      `· Offline workshops — Fixed-date, in-person events held in Korea`,
      `· Online live sessions — Real-time online sessions held on fixed dates and times`,
      `· Video courses — Recorded lectures available for viewing within the Service`,
      `· Downloadable materials — Files that can be saved to a user's device (e.g., PDF)`,
      `· View-only materials — Materials available for viewing within the Service only; not available for download`,
      `· Bundles — Packages combining two or more of the above products`,
      `· Career and settlement support tools (Pathfinder, etc.)`,
    ],
  },
  {
    heading: '9. Service hours and time zones',
    body: [
      `① The Service is provided on a 24/7 basis in principle, but may be restricted due to the Company's operational circumstances or the reasons set out in Article 19.`,
      ``,
      `② The dates and times of offline workshops and online live sessions are displayed in Korea Standard Time (KST, UTC+9). Users must confirm their local time before making a booking.`,
    ],
  },
  {
    heading: '10. What users must and must not do',
    body: [
      `① Users must provide accurate information, use the Service only for personal and lawful purposes, and treat other learners, creators, and Company personnel with respect.`,
      ``,
      `② Users must not engage in any of the following:`,
      `· Sharing their account so that others may access paid Content`,
      `· Copying, recording, downloading (except where permitted), reselling, or externally sharing paid Content without authorization`,
      `· Recording workshops or live sessions without permission`,
      `· Harassing, threatening, or otherwise causing harm to other users or Company personnel`,
      `· Using automated tools to gain unauthorized access to the Service, or disrupting or damaging the Service`,
      `· Providing false information, impersonating another person, or otherwise violating applicable law or these Terms`,
    ],
  },
  {
    heading: '11. Intellectual property and how you may use Content',
    body: [
      `① Intellectual property rights, including copyright, in the Service and its Content vest in the Company or the rightful rights holder.`,
      ``,
      `② By purchasing a product, a user is granted a personal, non-transferable license to use the Content. Users may not re-use, resell, redistribute, or copy or share Content outside the Service.`,
    ],
  },
  {
    heading: '12. Content you create',
    body: [
      `① Copyright in content posted by a user within the Service — such as questions or reviews — vests in that user.`,
      ``,
      `② By posting content, the user grants the Company permission to use that content within the Service for the purposes of operating and improving the Service. The Company may delete content or restrict its use if it violates applicable law or these Terms.`,
    ],
  },
  {
    chapter: 'Chapter 4 - Paid products, payment, and cancellation',
    heading: '13. Pricing and currency',
    body: [
      `① Some parts of the Service are provided on a paid basis. Prices are displayed in US dollars (USD). If a user selects a Korean card, the price is converted to Korean won (KRW) and the exact KRW amount is displayed before payment.`,
      ``,
      `② Korean cards are charged in KRW. Overseas cards and supported international payment methods may be charged in USD. Payments are processed through Eximbay and its supported payment partners. The Company does not store full card numbers on its own servers.`,
      ``,
      `③ For USD payments, the amount actually received or refunded may differ due to exchange rate fluctuations and payment provider fees. The Company will inform users of this before payment.`,
    ],
  },
  {
    heading: '14. Notice and consent regarding cancellation restrictions',
    body: [
      `① Users must agree to the refund policy for a product before completing payment. Payment cannot proceed without this agreement.`,
      ``,
      `② In accordance with Article 17(6) of the Act on Consumer Protection in Electronic Commerce, the Company will clearly display, in a location easily visible to the user (such as the payment screen), the fact that the right of cancellation is restricted once provision of Digital Content — including video courses, downloadable materials, and view-only materials — has commenced. The Company will also implement a confirmation step (such as a checkbox) for the user to acknowledge this before payment.`,
    ],
  },
  {
    heading: '15. Cancellation and refunds',
    body: [
      `① Pursuant to Article 17(1) of the Act on Consumer Protection in Electronic Commerce, a user may withdraw from a contract within 7 days of the date on which the user receives a written record of the contract or the date on which supply of the goods or service commences, whichever is earlier.`,
      ``,
      `② Once provision of Digital Content has commenced, the right of cancellation may be restricted. However, where a contract consists of Divisible Digital Content, the user may still cancel in respect of the portion for which provision has not yet commenced (e.g., unwatched lectures).`,
      ``,
      `③ The Company will review refund requests in cases of duplicate payment, fault on the part of the Company, technical error, or where required by applicable law. Refunds are processed in the currency in which payment was made (KRW or USD).`,
      ``,
      `④ Specific refund conditions by product type are set out in the refund policy published separately by the Company, which forms part of these Terms.`,
    ],
  },
  {
    heading:
      '16. Workshops and live sessions: bookings, cancellations, and no-shows',
    body: [
      `① A booking is confirmed upon completion of payment. If payment is not completed within the checkout window (15 minutes), the reserved seat will be released. When a session is sold out, the waitlist operates on a first-come, first-served basis. Notifications of an available seat are valid for a limited time (3 hours) only.`,
      ``,
      `② If the Company cancels an event, a full refund will be issued to all registered participants. If the Company changes the schedule, the user may either accept the new schedule or receive a full refund.`,
      ``,
      <>
        ③ If a user fails to attend without prior notice (no-show) or cancels
        before the event, refunds will be processed in accordance with any
        separately notified refund policy, which shall take precedence over the
        default schedule in these Terms. Where no separate{' '}
        <Link to='/refund-policy' className='underline'>
          refund policy
        </Link>{' '}
        has been notified, the following default schedule applies:
      </>,
      {
        type: 'table',
        headers: ['Cancellation timing', 'Refund'],
        rows: [
          ['More than 48 hours before the event', '100% of amount paid'],
          ['24 to 48 hours before the event', '50% of amount paid'],
          ['Less than 24 hours before the event', 'No refund'],
        ],
      },
    ],
  },
  {
    chapter: 'Chapter 5 - Your personal information and location data',
    heading: '17. How we protect your personal information',
    body: [
      <>
        ① The Company protects users&apos; personal information in accordance
        with the Personal Information Protection Act and other applicable law.
        Details are set out in the{' '}
        <Link to='/privacy' className='underline'>
          Privacy Policy
        </Link>
        .
      </>,
      ``,
      `② The Company obtains separate consent for required and optional items of personal information. Users may decline to provide optional items.`,
    ],
  },
  {
    heading: '18. How we handle location information',
    body: [
      `① For the purpose of providing services such as Pathfinder, the Company may collect the country and city of residence and the school location entered directly by the user. This information is used solely to assess visa eligibility and guide settlement pathways in Korea.`,
      ``,
      `② The Company does not conduct automatic location tracking using telecommunications infrastructure such as GPS, cell towers, or Wi-Fi. Accordingly, the information described in paragraph ① does not, in principle, constitute personal location information within the meaning of the Act on the Protection, Use, etc. of Location Information.`,
      ``,
      `③ The Company protects location-related information in accordance with the Personal Information Protection Act and will destroy it without delay once the purpose of collection has been achieved.`,
    ],
  },
  {
    chapter: 'Chapter 6 - Service changes, suspension, and liability',
    heading: '19. When and how we may change or suspend the Service',
    body: [
      <>
        ① The Company may modify, temporarily suspend, or terminate all or part
        of the Service, and will provide advance notice of material changes.
        Where paid products are affected, refunds will be issued in accordance
        with the{' '}
        <Link to='/refund-policy' className='underline'>
          refund policy
        </Link>
        .
      </>,
      ``,
      `② The Service may be temporarily restricted in cases of scheduled or emergency maintenance, network or power failures, force majeure events such as natural disasters, or other similar circumstances. The Company will notify users in advance or as soon as reasonably practicable after the fact.`,
    ],
  },
  {
    heading: '20. Limitations on our promises',
    body: [
      `① To the extent permitted by applicable law, the Company provides the Service "as is." The Company does not guarantee specific outcomes such as employment, visa issuance, or exam scores, nor does it guarantee that the Service will be available without interruption at all times.`,
      ``,
      `② Unless the Company is found to have acted with willful misconduct or gross negligence, the Company is not liable for any loss suffered by a user in the course of using content or services provided by other learners, creators, or Company personnel.`,
    ],
  },
  {
    heading: '21. Limits on our liability',
    body: [
      `① The Company's liability to a user for damages is limited to ordinary direct damages, capped at the amount the user directly paid to the Company for the service to which the loss relates.`,
      ``,
      `② Paragraph ① does not apply where the Company's willful misconduct or gross negligence is objectively established and a causal link to the loss is proven.`,
    ],
  },
  {
    heading: '22. Account restrictions and termination',
    body: [
      `① The Company may restrict a user's access to the Service or terminate the service agreement if the user violates these Terms or interferes with the operation of the Service.`,
      ``,
      `② When restricting access or terminating an agreement, the Company will, in principle, notify the user in advance of the reason and of how to raise an objection. However, in urgent situations or in cases of serious violation, notice may be given after the fact.`,
      ``,
      <>
        ③ Where a restriction or termination results in a user being unable to
        use a paid product for which they have already paid, the Company will
        process a refund in accordance with the{' '}
        <Link to='/refund-policy' className='underline'>
          refund policy
        </Link>{' '}
        and applicable law.
      </>,
      ``,
      `④ A user may stop using the Service and request deletion of their account (withdrawal from membership) at any time.`,
    ],
  },
  {
    chapter: 'Chapter 7 - International users and how disputes are resolved',
    heading: '23. Users outside Korea',
    body: [
      `The Service may be provided to users located outside the Republic of Korea, and the mandatory laws of the relevant country may apply. In such cases, the Company may provide separate guidance on the terms applicable to the service offered in that country.`,
    ],
  },
  {
    heading: '23. Governing law and dispute resolution',
    body: [
      `① These Terms are interpreted and applied in accordance with the laws of the Republic of Korea.`,
      ``,
      `② In the event of a dispute arising in connection with use of the Service, the parties shall make good-faith efforts to resolve the matter through consultation.`,
      ``,
      `⑤ Users may seek mediation or consultation through relevant bodies including the Consumer Dispute Mediation Committee, the Content Dispute Mediation Committee, and the Personal Information Dispute Mediation Committee (1833-6972).`,
      ``,
      <strong className={applyTypography('b16Strong')}>
        Supplementary Provisions
      </strong>,
      `① These Terms take effect from 17.July 2026.`,
      ``,
      `② The previous Terms of Service in effect prior to these Terms are replaced by these Terms.`,
      `Koready | contact@ko-ready.com | Business registration number: 179-45-01005`,
    ],
  },
];

/** 정책 문서 한 버전. PolicyDocument가 레지스트리를 통해 읽어 렌더링한다. */
export default {
  title: 'Terms of Use',
  lastUpdated: 'July 17, 2026',
  effectiveDate: '2026-07-17',
  sections,
};
